import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useBudgetAnalysis } from '../hooks/useBudgetAnalysis';
import { useProjects } from '../hooks/useProjects';
import { useTasks } from '../hooks/useTasks';
import { isDashboardShellPath } from '../constants/dashboardRoutes';

/**
 * ProjectContext - Centralized project data management
 * Eliminates prop drilling by providing project data, budget analysis, and permissions
 * through React context
 */
const ProjectContext = createContext();

export const useProjectContext = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('useProjectContext must be used within a ProjectProvider');
    }
    return context;
};

export const ProjectProvider = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();

    // Budget analysis requires project_manager/manager/director/admin/finance
    // (backend permission check in projects/views.py:budget_analysis_batch).
    // Disable proactively for roles that will 403, to prevent cascade errors.
    const BUDGET_ANALYSIS_ALLOWED_ROLES = new Set([
        'project_manager', 'manager', 'director', 'admin', 'finance',
    ]);
    const budgetAnalysisEnabled = useMemo(
        () => !isDashboardShellPath(location.pathname) &&
              (user?.is_superuser || BUDGET_ANALYSIS_ALLOWED_ROLES.has(user?.role)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname, user?.role, user?.is_superuser]
    );

    // Use existing hooks for data management
    const {
        projects,
        loading: projectsLoading,
        error: projectsError,
        createProject,
        updateProject,
        deleteProject,
        setProjects,
        fetchProjects
    } = useProjects(user);

    const { budgetAlerts, burnRateForecasts, loading: budgetLoading } = useBudgetAnalysis(
        projects,
        { enabled: budgetAnalysisEnabled }
    );
    const { createTask, updateTask, deleteTask } = useTasks(setProjects);

    // UI state
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);

    // Dialog states - centralized
    const [dialogStates, setDialogStates] = useState({
        projectDialog: false,
        taskDialog: false,
        assignmentDialog: false
    });

    // Computed permissions (centralized)
    const permissions = {
        canManageProjects: user?.role === 'project_manager' ||
            user?.role === 'admin' ||
            user?.role === 'manager' ||
            user?.role === 'director' ||
            user?.is_superuser,
        canAssignTasks: user?.role === 'project_manager' ||
            user?.role === 'admin' ||
            user?.is_superuser,
        canViewBudgets: user?.role !== 'employee' || user?.is_superuser
    };

    // Enhanced project data with budget information
    const enhancedProjects = projects.map(project => ({
        ...project,
        budgetAlerts: budgetAlerts[project.id] || [],
        burnRateForecast: burnRateForecasts[project.id],
        hasAlerts: (budgetAlerts[project.id] || []).length > 0,
        hasBudgetData: !!burnRateForecasts[project.id]
    }));

    // Dialog management
    const dialogOperations = {
        openProjectDialog: useCallback((project = null) => {
            setSelectedProject(project);
            setDialogStates(prev => ({ ...prev, projectDialog: true }));
        }, []),

        closeProjectDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, projectDialog: false }));
            setTimeout(() => setSelectedProject(null), 100); // Delay to prevent flicker
        }, []),

        openTaskDialog: useCallback((task = null, project = null) => {
            if (!task && project) {
                setSelectedProject(project);
            }
            setSelectedTask(task);
            setDialogStates(prev => ({ ...prev, taskDialog: true }));
        }, []),

        closeTaskDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, taskDialog: false }));
            setTimeout(() => {
                setSelectedTask(null);
                if (!selectedProject) setSelectedProject(null);
            }, 100);
        }, [selectedProject]),

        openAssignmentDialog: useCallback((task) => {
            setSelectedTask(task);
            setDialogStates(prev => ({ ...prev, assignmentDialog: true }));
        }, []),

        closeAssignmentDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, assignmentDialog: false }));
            setTimeout(() => setSelectedTask(null), 100);
        }, [])
    };

    // Project operations with context updates
    const projectOperations = {
        create: useCallback(async (projectData) => {
            const newProject = await createProject(projectData);
            return newProject;
        }, [createProject]),

        update: useCallback(async (projectId, projectData) => {
            const updatedProject = await updateProject(projectId, projectData);
            return updatedProject;
        }, [updateProject]),

        delete: useCallback(async (projectId) => {
            const result = await deleteProject(projectId);
            if (result?.success && selectedProject?.id === projectId) {
                setSelectedProject(null);
            }
            return result;
        }, [deleteProject, selectedProject?.id]),

        select: useCallback((project) => {
            setSelectedProject(project);
        }, []),

        clearSelection: useCallback(() => {
            setSelectedProject(null);
        }, []),

        refresh: useCallback(async () => {
            console.log('🔄 ProjectContext: Refreshing projects data');
            await fetchProjects(true); // Force refresh
        }, [fetchProjects])
    };

    // Task operations with context updates
    const taskOperations = {
        create: useCallback(async (taskData) => {
            const newTask = await createTask(taskData);
            return newTask;
        }, [createTask]),

        update: useCallback(async (taskId, taskData) => {
            const updatedTask = await updateTask(taskId, taskData);
            return updatedTask;
        }, [updateTask]),

        delete: useCallback(async (taskId) => {
            await deleteTask(taskId);
            // Clear selection if deleted task was selected
            if (selectedTask?.id === taskId) {
                setSelectedTask(null);
            }
        }, [deleteTask, selectedTask?.id]),

        select: useCallback((task) => {
            setSelectedTask(task);
        }, []),

        clearSelection: useCallback(() => {
            setSelectedTask(null);
        }, [])
    };

    // Utility functions
    const getProjectById = useCallback((projectId) => {
        return enhancedProjects.find(p => p.id === projectId);
    }, [enhancedProjects]);

    const getProjectsWithAlerts = useCallback(() => {
        return enhancedProjects.filter(p => p.hasAlerts);
    }, [enhancedProjects]);

    const getProjectsWithBudgetIssues = useCallback(() => {
        return enhancedProjects.filter(p =>
            p.burnRateForecast?.forecast_status === 'critical'
        );
    }, [enhancedProjects]);

    const contextValue = {
        // Data
        projects: enhancedProjects,
        selectedProject,
        selectedTask,

        // Loading states
        loading: projectsLoading || budgetLoading,
        error: projectsError,

        // Dialog states
        dialogStates,

        // Permissions
        permissions,

        // Operations
        projectOperations,
        taskOperations,
        dialogOperations,

        // Top-level CRUD aliases consumed by ProjectManagementPage and other components
        // that destructure directly from useProjectContext() rather than going through
        // projectOperations. Keep in sync with projectOperations.{create,update,delete}.
        createProject,
        updateProject,
        deleteProject,
        fetchProjects,

        // Utilities
        getProjectById,
        getProjectsWithAlerts,
        getProjectsWithBudgetIssues,

        // Raw data access (for backward compatibility)
        rawProjects: projects,
        budgetAlerts,
        burnRateForecasts
    };

    return (
        <ProjectContext.Provider value={contextValue}>
            {children}
        </ProjectContext.Provider>
    );
}; 